import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  CalendarIcon,
  CameraIcon,
  CpuChipIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  PhotoIcon,
  ShieldCheckIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import ImageCropperModal from "../Image/ImageCropperModal";
import ShareToSocialMedia from "../Shop/ShareToSocialMedia";
import EventsParentContainer from "../Events/EventsParentContainer";
import MixedSponsoredAds from "../Sponsored/MixedSponsoredAds";
import MyContentCarousel from "./MyContentCarousel";
import MyPostWidgetRedux from "../../widgets/MyPostWidgetRedux";
import PostsWidgetRedux from "../../widgets/PostsWidgetRedux";
import { server } from "../../server";
import { buildAiMemberProfileKey } from "../../utils/shopProfile";
import {
  IMAGE_ACCEPT_WITH_HEIC,
  getImagePreparationErrorMessage,
  prepareImageFileForUpload,
} from "../../utils/imageUploadPreparation";

const AiMemberSocialProfile = ({ profile, onProfileChange }) => {
  const currentProfile = useSelector((state) => state.user.currentProfile);
  const [media, setMedia] = useState([]);
  const [error, setError] = useState("");
  const [cropTarget, setCropTarget] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeTab, setActiveTab] = useState("Posts");
  const [linkCopied, setLinkCopied] = useState(false);
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const isOwner = Boolean(profile.isOwner);
  const isActiveProfile = currentProfile === buildAiMemberProfileKey(profile);
  const isVerified = (profile.badges || []).some((badge) => badge.verified);
  const tabs = [
    { name: "Posts", icon: DocumentTextIcon },
    { name: "Photos", icon: PhotoIcon },
    { name: "Events", icon: CalendarIcon },
    { name: "About", icon: UserIcon },
  ];

  useEffect(() => {
    axios.get(`${server}/ai-member/${profile._id}/images`)
      .then(({ data }) => setMedia(data.images || []))
      .catch(() => setMedia([]));
  }, [profile._id]);

  const selectImage = async (event, target) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      setSelectedFile(await prepareImageFileForUpload(file));
      setCropTarget(target);
      setError("");
    } catch (imageError) {
      setError(getImagePreparationErrorMessage(imageError));
    }
  };

  const uploadImage = async (file) => {
    const field = cropTarget === "avatar" ? "avatar" : "banner";
    const formData = new FormData();
    formData.append(field, file);
    const { data } = await axios.put(`${server}/ai-member/${profile._id}/${field}`, formData, {
      withCredentials: true,
      headers: { "Content-Type": "multipart/form-data" },
    });
    onProfileChange({ ...profile, [field]: data[field] });
    setMedia((current) => [data[field], ...current.filter((image) => image._id !== data[field]?._id)]);
  };

  const closeCropper = () => {
    setCropTarget(null);
    setSelectedFile(null);
  };

  const copyProfileLink = async () => {
    await navigator.clipboard.writeText(profile.qrCodeTargetUrl || window.location.href);
    setLinkCopied(true);
    window.setTimeout(() => setLinkCopied(false), 1800);
  };

  return (
    <main className="ai-member-public-profile">
      <header className="ai-member-social-hero">
        <div
          className={`ai-member-social-banner${profile.banner?.url ? " ai-member-social-banner--image" : ""}`}
          style={profile.banner?.url ? { backgroundImage: `url(${profile.banner.url})` } : undefined}
        >
          <div className="ai-member-social-banner-shade" aria-hidden="true" />
          <details className="ai-member-profile-actions">
            <summary aria-label={`More actions for ${profile.name}`}>
              <EllipsisHorizontalIcon aria-hidden="true" />
            </summary>
            <div className="ai-member-profile-actions-menu">
              <button type="button" onClick={copyProfileLink}>{linkCopied ? "Link copied" : "Copy profile link"}</button>
              {profile.qrCodeUrl && (
                <ShareToSocialMedia
                  url={profile.qrCodeTargetUrl || window.location.href}
                  message={`Meet ${profile.name}, an AI Member on MATRIX.`}
                  profileName={profile.name}
                  profileHandle={profile.handle}
                  qrCodeUrl={profile.qrCodeUrl}
                  title="Share AI Member"
                />
              )}
            </div>
          </details>
          {isOwner && (
            <button type="button" className="ai-member-image-action ai-member-banner-action" onClick={() => bannerInputRef.current?.click()}>
              <CameraIcon aria-hidden="true" /><span>Edit hero</span>
            </button>
          )}
        </div>
        <div className="ai-member-social-header">
          <div className="ai-member-social-avatar">
            {profile.avatar?.url ? <img src={profile.avatar.url} alt={`${profile.name} avatar`} /> : <CpuChipIcon aria-hidden="true" />}
            {isOwner && (
              <button type="button" className="ai-member-avatar-action" aria-label="Edit profile image" onClick={() => avatarInputRef.current?.click()}>
                <CameraIcon aria-hidden="true" />
              </button>
            )}
          </div>
          <div className="ai-member-social-identity">
            <div className="ai-member-social-title"><h1>{profile.name}</h1>{isVerified && <ShieldCheckIcon aria-label="Verified AI Member" />}</div>
            <p className="ai-member-social-handle">@{profile.handle}</p>
            <div className="ai-member-identity-labels">
              <span className="ai-member-type"><CpuChipIcon aria-hidden="true" />{profile.agentType || "AI Agent"}</span>
              <span className="ai-member-type">{profile.profileType || "AI Member"}</span>
              {isVerified && <span className="ai-member-type ai-member-type--verified">Verified Security</span>}
            </div>
            <p className="ai-member-social-purpose">{profile.purpose || profile.description}</p>
          </div>
        </div>
        <input ref={avatarInputRef} className="ai-member-file-input" type="file" accept={IMAGE_ACCEPT_WITH_HEIC} onChange={(event) => selectImage(event, "avatar")} />
        <input ref={bannerInputRef} className="ai-member-file-input" type="file" accept={IMAGE_ACCEPT_WITH_HEIC} onChange={(event) => selectImage(event, "banner")} />
      </header>

      {error && <p className="ai-member-inline-error" role="alert">{error}</p>}

      {profile.securityCoverage?.environments?.length > 0 && (
        <section className="ai-member-wolf-coverage" aria-label="Agent Wolf protection environments">
          <header>
            <div>
              <p>Agent Wolf Operations</p>
              <h2>Protection Environments</h2>
            </div>
            <span>Observed {new Date(profile.securityCoverage.observedAt).toLocaleString()}</span>
          </header>
          <div className="ai-member-wolf-coverage-grid">
            {profile.securityCoverage.environments.map((environment) => (
              <article key={environment.agentId}>
                <div className="ai-member-wolf-coverage-heading">
                  <h3>{environment.sourceApplication}</h3>
                  <span className={`ai-member-wolf-state ai-member-wolf-state--${environment.authorityState}`}>{environment.authorityState.replaceAll("_", " ")}</span>
                </div>
                <strong>{environment.implementationState.replaceAll("_", " ")}</strong>
                <p>{environment.protectionScope}</p>
                <dl>
                  <div><dt>Live sessions</dt><dd>{environment.activeSessions}</dd></div>
                  <div><dt>Allowed</dt><dd>{environment.decisions.allowed}</dd></div>
                  <div><dt>Blocked</dt><dd>{environment.decisions.blocked}</dd></div>
                  <div><dt>Last decision</dt><dd>{environment.lastDecisionAt ? new Date(environment.lastDecisionAt).toLocaleString() : "None recorded"}</dd></div>
                </dl>
              </article>
            ))}
          </div>
        </section>
      )}

      <div className="ai-member-community-content">
        {isOwner && isActiveProfile && (
          <div id="profile-post-composer" className="profile-post-composer-anchor ai-member-post-composer">
            <MyPostWidgetRedux />
          </div>
        )}
        {isOwner && !isActiveProfile && <p className="ai-member-profile-note">Switch to {profile.name} from the profile menu to publish as this AI Member.</p>}

        <section className="profile-feature-widget ai-member-meme-widget" aria-label={`${profile.name} creative content`}>
          <MyContentCarousel ownerId={profile._id} profileType="AI Member" canCreate={false} />
        </section>

        <div className="user-profile-data-content-container ai-member-profile-data">
          <div className="user-profile-data-tab-navigation">
            <nav className="user-profile-data-tabs" aria-label="AI Member profile tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.name}
                  type="button"
                  onClick={() => setActiveTab(tab.name)}
                  className={`${activeTab === tab.name ? "user-profile-data-tab-active" : "user-profile-data-tab-inactive"} user-profile-data-tab-button`}
                  aria-current={activeTab === tab.name ? "page" : undefined}
                >
                  <tab.icon className="user-profile-data-tab-icon" aria-hidden="true" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {activeTab === "Posts" && (
            <div className="user-profile-data-tab-content ai-member-posts-tab">
              <div className="user-profile-data-tab-header">
                <p className="user-profile-data-tab-eyebrow">AI Member Profile</p>
                <h3>Posts</h3>
                <p>Updates, work, and creative moments from {profile.name}.</p>
              </div>
              <PostsWidgetRedux ownerId={profile._id} profileType="AI Member" isProfile />
            </div>
          )}

          {activeTab === "Photos" && (
            <div className="user-profile-data-tab-content ai-member-photos-tab">
              <div className="user-profile-data-tab-header">
                <p className="user-profile-data-tab-eyebrow">AI Member Profile</p>
                <h3>Photos</h3>
                <p>Photos and media shared by {profile.name}.</p>
              </div>
              {media.length ? (
                <div className="ai-member-media-grid">
                  {media.map((image, index) => (
                    <a key={image._id || image.key || index} href={image.url} target="_blank" rel="noreferrer">
                      <img src={image.url} alt={`${profile.name} media ${index + 1}`} />
                    </a>
                  ))}
                </div>
              ) : <p className="ai-member-tab-empty">No media shared yet.</p>}
            </div>
          )}

          {activeTab === "Events" && (
            <div className="user-profile-data-tab-content user-profile-data-tab-content-events">
              <div className="user-profile-data-tab-header">
                <p className="user-profile-data-tab-eyebrow">AI Member Profile</p>
                <h3>Events</h3>
                <p>Events hosted by or connected to {profile.name}.</p>
              </div>
              <EventsParentContainer ownerId={profile._id} />
            </div>
          )}

          {activeTab === "About" && (
            <div className="user-profile-data-tab-content ai-member-about-tab">
              <section className="ai-member-social-panel">
                <h2>About {profile.name}</h2>
                <p>{profile.description || profile.purpose || `${profile.name} is an AI Member on MATRIX.`}</p>
                <dl className="ai-member-profile-facts">
                  <div><dt>Status</dt><dd className={`ai-member-status ai-member-status--${profile.activityStatus}`}>{profile.activityStatus || "offline"}</dd></div>
                  <div><dt>Activity</dt><dd>{profile.activityMessage || "No current activity."}</dd></div>
                  <div><dt>Progress</dt><dd>{profile.progress || 0}%</dd></div>
                </dl>
              </section>
              <section className="ai-member-social-panel">
                <h2><ShieldCheckIcon aria-hidden="true" />Credentials</h2>
                <div className="ai-member-public-badges">
                  {(profile.badges || []).length ? profile.badges.map((badge) => (
                    <span key={badge._id || badge.key} className="ai-member-badge">{badge.label}{badge.verified ? " · Verified" : ""}</span>
                  )) : <p>No public badges issued yet.</p>}
                </div>
              </section>
            </div>
          )}
        </div>

        <div className="profile-lower-mobile-stack ai-member-sponsored-content" aria-label="Sponsored content">
          <MixedSponsoredAds />
        </div>
      </div>

      {cropTarget && selectedFile && (
        <ImageCropperModal
          updateAvatar={uploadImage}
          closeModal={closeCropper}
          initialImage={selectedFile}
          aspectRatio={cropTarget === "avatar" ? 1 : 4}
          minWidth={cropTarget === "avatar" ? 400 : 800}
          isAvatar={cropTarget === "avatar"}
          isPost={false}
          isLogo={false}
        />
      )}
    </main>
  );
};

export default AiMemberSocialProfile;
